import { useEffect, useState } from 'react';

import { animationPlayerStore, sceneSettingsStore } from 'stores';

const CANVAS_HEIGHT_QUERY_PARAM_NAME = 'canvasHeight';
const CANVAS_WIDTH_QUERY_PARAM_NAME = 'canvasWidth';

export default function useCanvasResizer(aspectRatio: number) {
  const [_, setCanvasHolderSize] = useState({
    width: 0,
    height: 0,
  });
  const threeJSDiv = document.getElementById('threejs-div');
  const canvasHolder = document.getElementById('r3f-canvas-holder');

  const params = new URLSearchParams(window.location.search);
  const canvasHeight = params.get(CANVAS_HEIGHT_QUERY_PARAM_NAME);
  const canvasWidth = params.get(CANVAS_WIDTH_QUERY_PARAM_NAME);

  function roundToNearestEven(number: number): number {
    /**
     * Round numbers to the nearest even number.
     * Example:
     * 10.25 -> 10
     * 10.75 -> 10
     * 11.25 -> 12
     * 11.75 -> 12
     */
    const floor = Math.floor(number);
    const fractionalPart = number - floor;

    // If fractional part is less than 0.5, round down, otherwise round up
    const rounded = fractionalPart < 0.5 ? floor : floor + 1;

    // Make the rounded number even
    return rounded % 2 === 0
      ? rounded
      : fractionalPart < 0.5
        ? rounded + 1
        : rounded - 1;
  }

  useEffect(() => {
    if (!threeJSDiv || !canvasHolder) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setCanvasHolderSize({ width, height });
      }
    });
    resizeObserver.observe(threeJSDiv);
    return () => resizeObserver.disconnect();
  }, [threeJSDiv, canvasHolder]);

  useEffect(() => {
    if (!threeJSDiv) return;
    if (!canvasHolder) return;

    // If user has set canvasHeight and canvasWidth query params, use those
    // Primarily used by post processing thumbnail generation
    if (canvasHeight && canvasWidth) {
      canvasHolder.style.width = `${canvasWidth}px`;
      canvasHolder.style.height = `${canvasHeight}px`;
      sceneSettingsStore.set(
        'backgroundSettings',
        'canvasWidth',
        parseInt(canvasWidth),
      );
      sceneSettingsStore.set(
        'backgroundSettings',
        'canvasHeight',
        parseInt(canvasHeight),
      );
      return;
    }

    // ffmpeg requires even number of pixels to function properly
    const maxWidth = roundToNearestEven(threeJSDiv.clientWidth);
    const maxHeight = roundToNearestEven(threeJSDiv.clientHeight);

    // Determine if width or height is the limiting factor for resizing
    const useWidthAsMax = maxHeight * aspectRatio > maxWidth;

    let newWidth = undefined;
    let newHeight = undefined;
    if (aspectRatio === -1) {
      newWidth = maxWidth;
      newHeight = maxHeight;
    } else if (useWidthAsMax) {
      newWidth = maxWidth;
      newHeight = roundToNearestEven(maxWidth / aspectRatio);
    } else {
      newWidth = roundToNearestEven(maxHeight * aspectRatio);
      newHeight = maxHeight;
    }
    canvasHolder.style.width = `${newWidth}px`;
    canvasHolder.style.height = `${newHeight}px`;
    sceneSettingsStore.set('backgroundSettings', 'canvasWidth', newWidth);
    sceneSettingsStore.set('backgroundSettings', 'canvasHeight', newHeight);
  });
}
